import { Col, Modal, Row, message } from 'antd';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Document, Page } from 'react-pdf';
import { DocumentCallback } from 'react-pdf/dist/cjs/shared/types';
import receivableNotificationBuildApi from '../../../../apis/ReceivableNotificationBuildApi';
import receivableNotificationSignApi from '../../../../apis/ReceivableNotificationSignApi';
import { Receivable, ReceivableNotificationBuild, ReceivableNotificationSign } from '../../../../models/Entities';
import alertService from '../../../../services/AlertService';
import receivableService from '../../../../services/ReceivableService';
import styles from './ReceivableNotificationSignModal.module.scss';

const ReceivableNotificationSignModal = (props: Props) => {
    const { receivable, notificationBuild } = props;

    /*** HOOKS ***/

    const intl = useIntl();
    const [loading, setLoading] = useState<'loading' | 'saving'>();
    const [notificationSign, setNotificationSign] = useState<ReceivableNotificationSign>();
    const [document, setDocument] = useState<Blob>();
    const [pages, setPages] = useState<number>(0);

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                const document: Blob = await receivableNotificationBuildApi.download(notificationBuild.id!);
                const notificationSign: ReceivableNotificationSign = {
                    receivableId: receivable.id
                };
                setDocument(document);
                setNotificationSign(notificationSign);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [intl, notificationBuild.id, receivable]);

    /*** METHODS ***/

    const sign = async () => {
        try {
            setLoading('saving');
            if (notificationSign && document) {
                const AutoScript = window.AutoScript as any;
                const documentBase64 = await receivableService.blobToBase64(document);
                const formattedDocumentBase64: string = (documentBase64 as string).replace('data:text/xml;base64,', '');
                const parameters: string[] = [
                    'signaturePositionOnPageLowerLeftX = 25',
                    'signaturePositionOnPageLowerLeftY = 40',
                    'signaturePositionOnPageUpperRightX = 330',
                    'signaturePositionOnPageUpperRightY = 65',
                    'signaturePages = 1',
                    'layer2FontSize = 8'
                ];
                const formattedParameters: string = parameters.join('\n');

                AutoScript.sign(
                    formattedDocumentBase64,
                    'SHA512withRSA',
                    'PAdES',
                    formattedParameters,
                    (signatureB64: string, certificateB64: string, extraData: any) => {
                        const signedDocument = receivableService.b64toBlob(signatureB64, 'application/pdf');
                        save(notificationSign, signedDocument);
                        // const blobUrl = URL.createObjectURL(signedDocument);
                        // (window as any).location = blobUrl;
                    },
                    (type: string, error: string) => {
                        message.error(error);
                    }
                );

                // save(notificationSign, document);
            }
        } catch (error: any) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    const save = async (notificationSign: ReceivableNotificationSign, signedDocument: Blob) => {
        try {
            await receivableNotificationSignApi.create(notificationSign, signedDocument);
            await props.onSave();
            message.success(intl.formatMessage({ id: 'status.saved' }));
        } catch (error: any) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    // cancel
    const cancel = async () => {
        props.onCancel();
    };

    const initDocument = (document: DocumentCallback) => {
        setPages(document.numPages);
    };

    /*** VISUAL ***/

    return (
        <Modal
            title={<FormattedMessage id="receivable.notificationSign.modal.title" />}
            open={true}
            onCancel={cancel}
            onOk={sign}
            okText={<FormattedMessage id="button.sign" tagName="span" />}
            okButtonProps={{ loading: loading === 'saving', size: 'large' }}
            cancelButtonProps={{ disabled: loading === 'saving', size: 'large' }}
            width={1000}
        >
            <Row gutter={[28, 0]}>
                <Col span={24}>
                    <div className={styles.document}>
                        {document && (
                            <Document file={document} onLoadSuccess={initDocument}>
                                {Array.from(new Array(pages), (el, index) => (
                                    <Page key={`page${index + 1}`} pageNumber={index + 1} width={935} />
                                ))}
                            </Document>
                        )}
                    </div>
                </Col>
            </Row>
        </Modal>
    );
};
export default ReceivableNotificationSignModal;

interface Props {
    receivable: Receivable;
    notificationBuild: ReceivableNotificationBuild;
    onSave: () => Promise<void>;
    onCancel: () => void;
}
